import { filter, isEmpty, map, pathOr, pick, pipe } from 'ramda'
import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  makeApiTypes,
  mapObjectKeys,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'
import { convertToISOString } from 'sidecar-utilities'

export const types = {
  ...makeApiTypes('FETCH_SUBSCRIPTION_INFO', 'subscription'),
  ...makeApiTypes('FETCH_BILLING_HISTORY', 'subscription'),
  ...makeApiTypes('FETCH_MEMBER_BILLING_INFO', 'subscription'),
  ...makeApiTypes('FETCH_TRANSACTION_GROUPING', 'subscription'),
  RESET_TRANSACTION_SEARCH: 'subscription/RESET_TRANSACTION_SEARCH',
}

export const fetchBillingHistory = (input = {}, resetPagination = false) => ({
  [RSAA]: {
    endpoint: ({ Subscription }) => {
      const accountUuid = webStorage.getItem('aud')
      const size = Subscription?.billingHistory?.size

      // If there are no filters applied or filters have changed, start at page 0
      // Otherwise, increment the current page
      let page = 0
      if (!resetPagination && Subscription?.billingHistory?.page !== null) {
        page = Subscription?.billingHistory?.page + 1
      }

      const filterParams = pipe(
        mapObjectKeys({
          page,
          size,
          accountUuid,
          category: input.category,
          endDate: input.endDate
            ? convertToISOString(input.endDate)
            : undefined,
          memberUuid: input.member,
          paymentMethodUuid: input.paymentMethod,
          startDate: input.startDate
            ? convertToISOString(input.startDate)
            : undefined,
          type: input.billingType,
        }),
        // remove undefined keys
        pick([
          'page',
          'size',
          'accountUuid',
          'category',
          'endDate',
          'memberUuid',
          'paymentMethodUuid',
          'startDate',
          'type',
        ]),
      )({})

      return getApiPath(
        `exp`,
        `members/subscription/transactions/grouping${makeQueryParams(
          filterParams,
        )}`,
      )
    },
    method: 'GET',
    types: [
      {
        type: types.FETCH_BILLING_HISTORY_REQUEST,
        meta: { resetPagination },
      },
      types.FETCH_BILLING_HISTORY_SUCCESS,
      types.FETCH_BILLING_HISTORY_ERROR,
    ],
  },
})

export const fetchMembersBillingInfo = () => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `members/subscription${makeQueryParams({
        accountUuid: webStorage.getItem('aud'),
      })}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_MEMBER_BILLING_INFO_REQUEST,
      types.FETCH_MEMBER_BILLING_INFO_SUCCESS,
      types.FETCH_MEMBER_BILLING_INFO_ERROR,
    ],
  },
})

export const resetTransactionSearch = () => ({
  type: types.RESET_TRANSACTION_SEARCH,
})

const initialState = {
  billingHistory: {
    busy: false,
    error: '',
    entities: [],
    page: null,
    size: 25,
    isLastPageReached: false,
  },
  subscriptions: {
    busy: false,
    error: '',
    entities: [],
  },
}

// Selectors
export const getPremiumAutopaymentSchedule = (state = initialState) => {
  const primarySubscription = state.subscriptions.entities.find(
    subscription => {
      return subscription.primary === true
    },
  )

  return primarySubscription ? primarySubscription : {}
}

export const getActiveSubscriptions = (state = initialState) => {
  const activeSubscriptions = state.subscriptions.entities.filter(
    s => s.active === true || Math.abs(s.credit) !== 0,
  )

  return activeSubscriptions ? activeSubscriptions : {}
}

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.RESET_TRANSACTION_SEARCH: {
      return {
        ...state,
        billingHistory: initialState.billingHistory,
      }
    }

    // -----------------------

    case types.FETCH_BILLING_HISTORY_REQUEST: {
      return {
        ...state,
        billingHistory: {
          ...state.billingHistory,
          busy: true,
          error: '',
          // Clear entities immediately if this is a reset pagination request
          entities: meta.resetPagination ? [] : state.billingHistory.entities,
        },
      }
    }

    case types.FETCH_BILLING_HISTORY_SUCCESS: {
      const { content = [], page, size } = payload

      // Check if this is a fresh request (page 0) or a pagination request
      const isFilteredRequest = page === 0

      // Filter the response for ACA user
      // We decided to filter the response instead of modifying
      // The end point, because there are other use cases where we might
      // Need to use the unfiltered data
      const billingHistory = map(v => {
        if (v.subTransactions) {
          let b = filter(n => {
            return n.amount !== 0
          }, v.subTransactions)
          v.subTransactions = b
          // After filtering, if we end up with an empty subtransaction
          // Then clear out this record all together, we don't need to display it
          if (b.length === 0) return false

          // After filtering, if there is only one subtransaction
          // Then modify the structure of the response as if it's
          // a single transaction to begin with
          if (b.length === 1) {
            v.grouped = false
            const { paymentMethod, memberUuid, firstName, lastName } = b[0]
            v = { ...v, paymentMethod, memberUuid, firstName, lastName }
          }
        }
        return v
      }, content)

      const filteredBillingHistory = filter(n => n, billingHistory)

      return {
        ...state,
        billingHistory: {
          ...state.billingHistory,
          size: size,
          page: page,
          isLastPageReached: isEmpty(filteredBillingHistory),
          busy: false,
          // If this is a filtered request (page 0), replace the entities
          // Otherwise, append them
          entities: isFilteredRequest
            ? filteredBillingHistory
            : Object.values(
                [
                  ...state.billingHistory.entities,
                  ...filteredBillingHistory,
                ].reduce((acc, billingItem) => {
                  let { _uuid } = billingItem
                  return { ...acc, [_uuid]: billingItem }
                }, {}),
              ),
        },
      }
    }

    case types.FETCH_BILLING_HISTORY_ERROR: {
      return {
        ...state,
        billingHistory: {
          ...state.billingHistory,
          busy: false,
          error: payload.response.message,
        },
      }
    }

    // -----------------------

    case types.FETCH_MEMBER_BILLING_INFO_REQUEST: {
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          busy: true,
        },
      }
    }

    case types.FETCH_MEMBER_BILLING_INFO_SUCCESS: {
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          busy: false,
          entities: payload,
        },
      }
    }

    case types.FETCH_MEMBER_BILLING_INFO_ERROR: {
      return {
        ...state,
        subscriptions: {
          ...state.subscriptions,
          busy: false,
          error: pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    // -----------------------

    default:
      return state
  }
}
